import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { EmptyState } from '../ui';
import {
  Calculator,
  Plus,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Loader2,
  Hash,
  BarChart3,
  Sigma,
  Divide,
  X,
  Info
} from 'lucide-react';

const SCORING_TYPES = [
  { value: 'summed', label: 'Summed Score', icon: Sigma, description: 'Add up all response values' },
  { value: 'mean', label: 'Mean (Total Items)', icon: Divide, description: 'Sum ÷ total items defined' },
  { value: 'mean_answered', label: 'Mean (Answered)', icon: Divide, description: 'Sum ÷ items answered' },
  { value: 'irt', label: 'IRT-Based', icon: BarChart3, description: 'Item Response Theory scoring' }
];

const ScoringTab = ({ assessment }) => {
  const [scoringRules, setScoringRules] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // Use uuid for API URL paths
  const assessmentId = assessment?.uuid || assessment?.assessment_uuid;

  useEffect(() => {
    if (assessmentId) {
      fetchScoringRules();
      fetchQuestions();
    }
  }, [assessmentId]);

  const fetchScoringRules = async () => {
    try {
      const response = await api.get(`/api/assessment-builder/assessments/${assessmentId}/scoring-rules/`);
      // API returns { scoring_rules: [...] }
      const rules = response.data?.scoring_rules || response.data?.results || [];
      setScoringRules(Array.isArray(rules) ? rules : []);
    } catch (error) {
      console.error('Error fetching scoring rules:', error);
      toast.error('Failed to load scoring rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      // Try assessment questions endpoint first (includes items from canvas)
      const response = await api.get(`/api/assessment-builder/assessments/${assessmentId}/questions/`);
      const questionsData = response.data?.questions || response.data?.results || response.data || [];
      
      // The old survey-questions fallback called a route the API does not
      // have, so it never returned anything (FECOA-2175).
      if (Array.isArray(questionsData) && questionsData.length > 0) {
        setQuestions(questionsData);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      // Don't toast - questions are optional for viewing rules
    }
  };

  const handleAddRule = () => {
    setEditingRule(null);
    setShowModal(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setShowModal(true);
  };

  const handleDeleteRule = async (rule) => {
    if (!confirm(`Delete scoring rule "${rule.name}"? This cannot be undone.`)) return;
    
    setDeleting(rule.id);
    try {
      await api.delete(`/api/assessment-builder/scoring-rules/${rule.id}/`);
      setScoringRules(prev => prev.filter(r => r.id !== rule.id));
      toast.success('Scoring rule deleted');
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error('Failed to delete scoring rule');
    } finally {
      setDeleting(null);
    }
  };

  const handleSaveRule = async (formData) => {
    try {
      if (editingRule) {
        const response = await api.patch(`/api/assessment-builder/scoring-rules/${editingRule.id}/`, formData);
        setScoringRules(prev => prev.map(r => r.id === editingRule.id ? response.data : r));
        toast.success('Scoring rule updated');
      } else {
        const response = await api.post(`/api/assessment-builder/assessments/${assessmentId}/scoring-rules/`, formData);
        setScoringRules(prev => [...prev, response.data]);
        toast.success('Scoring rule created');
      }
      setShowModal(false);
      setEditingRule(null);
    } catch (error) {
      console.error('Error saving rule:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.code?.[0] || 'Failed to save scoring rule';
      toast.error(errorMsg);
      throw error;
    }
  };

  const getTypeInfo = (type) => SCORING_TYPES.find(t => t.value === type) || SCORING_TYPES[0];

  const handleToggleActive = async (rule) => {
    try {
      const response = await api.patch(`/api/assessment-builder/scoring-rules/${rule.id}/`, {
        is_active: !rule.is_active
      });
      setScoringRules(prev => prev.map(r => r.id === rule.id ? response.data : r));
      toast.success(response.data.is_active ? 'Scoring rule activated' : 'Scoring rule deactivated');
    } catch (error) {
      console.error('Error toggling rule:', error);
      toast.error('Failed to update scoring rule');
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center justify-center py-12"
      >
        <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading scoring rules...</span>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-50">
            <Calculator className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            Scoring Rules
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Define how scores are calculated when participants complete this assessment
          </p>
        </div>
        <button
          onClick={handleAddRule}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4" />
          Add Rule
        </button>
      </div>

      {/* Rules List */}
      {scoringRules.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={Calculator}
            title="No scoring rules defined"
            description="Add scoring rules to automatically calculate scores when participants complete this assessment."
            action={
              <button
                onClick={handleAddRule}
                className="btn btn-secondary"
              >
                <Plus className="h-4 w-4" />
                Add Scoring Rule
              </button>
            }
          />
        </div>
      ) : (
        <div className="space-y-3">
          {scoringRules.map((rule) => {
            const typeInfo = getTypeInfo(rule.scoring_type);
            const TypeIcon = typeInfo.icon;
            const itemCount = rule.included_questions?.length || 0;
            
            return (
              <div
                key={rule.id}
                className="card p-4 transition-colors hover:border-gray-300 dark:hover:border-white/15"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className={`flex-shrink-0 rounded-lg p-2 ${rule.is_active ? 'bg-primary-50 dark:bg-primary-400/10' : 'bg-gray-100 dark:bg-white/[0.06]'}`}>
                      <TypeIcon className={`h-5 w-5 ${rule.is_active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`} />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">{rule.name}</h4>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-gray-500 dark:text-gray-400">
                        <span className="inline-flex items-center gap-1">
                          <Hash className="h-3.5 w-3.5" />
                          {rule.code}
                        </span>
                        <span>•</span>
                        <span>{typeInfo.label}</span>
                        <span>•</span>
                        <span>{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                      </div>
                      {rule.description && (
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{rule.description}</p>
                      )}
                      {rule.interpretation_ranges?.length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-1">
                          {rule.interpretation_ranges.map((range, idx) => (
                            <span
                              key={idx}
                              className="badge badge-gray"
                            >
                              {range.min}-{range.max}: {range.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button
                      onClick={() => handleToggleActive(rule)}
                      className={`relative mr-1 h-5 w-9 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800 ${
                        rule.is_active ? 'bg-primary-600 dark:bg-primary-500' : 'bg-gray-200 dark:bg-white/15'
                      }`}
                      title={rule.is_active ? 'Click to deactivate' : 'Click to activate'}
                    >
                      <span
                        className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm ring-1 ring-gray-900/5 transition-transform duration-150 ${
                          rule.is_active ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    </button>
                    <button
                      onClick={() => handleEditRule(rule)}
                      className="btn btn-ghost btn-sm btn-icon"
                      title="Edit rule"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteRule(rule)}
                      disabled={deleting === rule.id}
                      className="btn btn-sm btn-icon text-gray-500 hover:bg-danger-50 hover:text-danger-600 dark:text-gray-400 dark:hover:bg-danger-500/10 dark:hover:text-danger-400"
                      title="Delete rule"
                    >
                      {deleting === rule.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info Box */}
      <div className="flex items-start gap-3 rounded-lg border border-info-500/25 bg-info-50 p-4 dark:bg-info-500/10">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-info-600 dark:text-info-400" />
        <div className="text-sm text-info-800 dark:text-info-400">
          <p className="font-medium">How scoring works</p>
          <p className="mt-1 text-info-700 dark:text-info-400">
            Scores are automatically calculated when participants complete surveys using this assessment. 
            View calculated scores in the Data Export section or via the Participant Scores API.
          </p>
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <ScoringRuleModal
            rule={editingRule}
            questions={questions}
            onSave={handleSaveRule}
            onClose={() => {
              setShowModal(false);
              setEditingRule(null);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const ScoringRuleModal = ({ rule, questions, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: rule?.name || '',
    code: rule?.code || '',
    description: rule?.description || '',
    scoring_type: rule?.scoring_type || 'summed',
    included_questions: rule?.included_questions || [],
    item_weights: rule?.item_weights || {},
    rescale_multiplier: rule?.rescale_multiplier || '',
    rescale_offset: rule?.rescale_offset || '',
    interpretation_ranges: rule?.interpretation_ranges || [],
    is_active: rule?.is_active ?? true
  });
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(
    !!(rule?.rescale_multiplier || rule?.rescale_offset || rule?.interpretation_ranges?.length || (rule?.item_weights && Object.keys(rule.item_weights).length > 0))
  );
  const [useCustomWeights, setUseCustomWeights] = useState(
    !!(rule?.item_weights && Object.keys(rule.item_weights).length > 0)
  );
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.code.trim()) newErrors.code = 'Code is required';
    if (!/^[A-Z0-9_]+$/.test(formData.code)) newErrors.code = 'Code must be uppercase letters, numbers, and underscores only';
    if (formData.included_questions.length === 0) newErrors.questions = 'Select at least one question';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      // Convert item_weights string values to numbers
      const cleanedWeights = {};
      for (const [qId, weights] of Object.entries(formData.item_weights)) {
        cleanedWeights[qId] = {};
        for (const [respVal, weight] of Object.entries(weights)) {
          cleanedWeights[qId][respVal] = weight === '' ? Number(respVal) : Number(weight);
        }
      }
      
      const payload = {
        ...formData,
        item_weights: cleanedWeights,
        rescale_multiplier: formData.rescale_multiplier || null,
        rescale_offset: formData.rescale_offset || null
      };
      await onSave(payload);
    } catch (error) {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };

  const toggleQuestion = (questionId) => {
    setFormData(prev => ({
      ...prev,
      included_questions: prev.included_questions.includes(questionId)
        ? prev.included_questions.filter(id => id !== questionId)
        : [...prev.included_questions, questionId]
    }));
  };

  const selectAllQuestions = () => {
    setFormData(prev => ({
      ...prev,
      included_questions: questions.map(q => q.id)
    }));
  };

  const deselectAllQuestions = () => {
    setFormData(prev => ({
      ...prev,
      included_questions: []
    }));
  };

  const addInterpretationRange = () => {
    setFormData(prev => ({
      ...prev,
      interpretation_ranges: [
        ...prev.interpretation_ranges,
        { min: 0, max: 0, label: '' }
      ]
    }));
  };

  const updateInterpretationRange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      interpretation_ranges: prev.interpretation_ranges.map((range, i) =>
        i === index ? { ...range, [field]: field === 'label' ? value : Number(value) } : range
      )
    }));
  };

  const removeInterpretationRange = (index) => {
    setFormData(prev => ({
      ...prev,
      interpretation_ranges: prev.interpretation_ranges.filter((_, i) => i !== index)
    }));
  };

  const getQuestionResponseOptions = (question) => {
    // Get response options from question - support various formats
    const options = question.response_options || question.responseOptions || question.options || [];
    if (Array.isArray(options)) return options;
    return [];
  };

  const updateItemWeight = (questionId, responseValue, weight) => {
    setFormData(prev => {
      const newWeights = { ...prev.item_weights };
      if (!newWeights[questionId]) {
        newWeights[questionId] = {};
      }
      // Store raw value during editing to allow backspace/clearing
      // Will be converted to number when form is submitted
      newWeights[questionId][String(responseValue)] = weight === '' ? '' : weight;
      return { ...prev, item_weights: newWeights };
    });
  };

  const resetQuestionWeights = (questionId) => {
    setFormData(prev => {
      const newWeights = { ...prev.item_weights };
      delete newWeights[questionId];
      return { ...prev, item_weights: newWeights };
    });
  };

  const toggleCustomWeights = (enabled) => {
    setUseCustomWeights(enabled);
    if (!enabled) {
      setFormData(prev => ({ ...prev, item_weights: {} }));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="surface-overlay flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="card-band">
          <div className="flex items-center gap-3">
            <Calculator className="h-5 w-5 text-white" />
            <h3 className="card-band-title">
              {rule ? 'Edit Scoring Rule' : 'Add Scoring Rule'}
            </h3>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Name & Code */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">
                Name <span className="text-danger-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className={`input ${
                  errors.name ? 'input-error' : ''
                }`}
                placeholder="e.g., Total Score"
              />
              {errors.name && <p className="field-error">{errors.name}</p>}
            </div>
            <div>
              <label className="label">
                Code <span className="text-danger-500">*</span>
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                className={`input font-mono ${
                  errors.code ? 'input-error' : ''
                }`}
                placeholder="e.g., TOTAL"
              />
              {errors.code && <p className="field-error">{errors.code}</p>}
              <p className="hint">Short code for exports</p>
            </div>
          </div>

          {/* Scoring Type */}
          <div>
            <label className="label mb-2">Scoring Type</label>
            <div className="grid grid-cols-2 gap-2">
              {SCORING_TYPES.map((type) => {
                const TypeIcon = type.icon;
                const isSelected = formData.scoring_type === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, scoring_type: type.value }))}
                    className={`rounded-lg border p-3 text-left transition-colors ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50/40 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:ring-primary-400'
                        : 'border-gray-200/80 bg-white hover:border-gray-300 dark:border-white/[0.07] dark:bg-gray-800 dark:hover:border-white/15'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <TypeIcon className={`h-4 w-4 ${isSelected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`} />
                      <span className={`text-sm font-medium ${isSelected ? 'text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-200'}`}>
                        {type.label}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="input resize-none"
              placeholder="Optional description of this scoring rule"
            />
          </div>

          {/* Questions */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="label mb-0">
                Questions to Include <span className="text-danger-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={selectAllQuestions}
                  className="link text-xs"
                >
                  Select All
                </button>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <button
                  type="button"
                  onClick={deselectAllQuestions}
                  className="text-xs font-medium text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Clear
                </button>
              </div>
            </div>
            {errors.questions && <p className="field-error mb-2 mt-0">{errors.questions}</p>}
            <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 dark:border-white/10">
              {questions.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No questions found in this assessment
                </div>
              ) : (
                questions.map((question, index) => {
                  const isSelected = formData.included_questions.includes(question.id);
                  return (
                    <label
                      key={question.id}
                      className={`flex cursor-pointer items-start gap-3 border-b border-gray-100 p-3 transition-colors last:border-b-0 hover:bg-gray-50 dark:border-white/[0.06] dark:hover:bg-white/[0.03] ${
                        isSelected ? 'bg-primary-50/60 dark:bg-primary-400/10' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleQuestion(question.id)}
                        className="mt-0.5 h-4 w-4 flex-shrink-0 rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Q{index + 1}:</span>{' '}
                        <span className="text-sm text-gray-900 line-clamp-2 dark:text-gray-100">
                          {question.question_text || question.text || 'Untitled question'}
                        </span>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
            <p className="hint">
              {formData.included_questions.length} of {questions.length} selected
            </p>
          </div>

          {/* Advanced Options */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
            >
              {showAdvanced ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Advanced Options
            </button>
            
            {showAdvanced && (
              <div className="mt-4 space-y-4 border-l-2 border-gray-100 pl-6 dark:border-white/10">
                {/* Rescaling */}
                <div>
                  <label className="label mb-2">Rescaling (optional)</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Multiplier</label>
                      <input
                        type="number"
                        step="any"
                        value={formData.rescale_multiplier}
                        onChange={(e) => setFormData(prev => ({ ...prev, rescale_multiplier: e.target.value }))}
                        className="input"
                        placeholder="e.g., 10"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Offset</label>
                      <input
                        type="number"
                        step="any"
                        value={formData.rescale_offset}
                        onChange={(e) => setFormData(prev => ({ ...prev, rescale_offset: e.target.value }))}
                        className="input"
                        placeholder="e.g., 50"
                      />
                    </div>
                  </div>
                  <p className="hint">
                    Formula: rescaled = (raw × multiplier) + offset
                  </p>
                </div>

                {/* Interpretation Ranges */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="label mb-0">Interpretation Ranges</label>
                    <button
                      type="button"
                      onClick={addInterpretationRange}
                      className="link inline-flex items-center gap-1 text-xs"
                    >
                      <Plus className="h-3 w-3" /> Add Range
                    </button>
                  </div>
                  {formData.interpretation_ranges.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No interpretation ranges defined</p>
                  ) : (
                    <div className="space-y-2">
                      {formData.interpretation_ranges.map((range, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="number"
                            value={range.min}
                            onChange={(e) => updateInterpretationRange(index, 'min', e.target.value)}
                            className="input h-8 w-20 px-2"
                            placeholder="Min"
                          />
                          <span className="text-gray-400 dark:text-gray-500">-</span>
                          <input
                            type="number"
                            value={range.max}
                            onChange={(e) => updateInterpretationRange(index, 'max', e.target.value)}
                            className="input h-8 w-20 px-2"
                            placeholder="Max"
                          />
                          <input
                            type="text"
                            value={range.label}
                            onChange={(e) => updateInterpretationRange(index, 'label', e.target.value)}
                            className="input h-8 flex-1 px-2"
                            placeholder="Label (e.g., Minimal)"
                          />
                          <button
                            type="button"
                            onClick={() => removeInterpretationRange(index)}
                            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-danger-50 hover:text-danger-600 dark:text-gray-500 dark:hover:bg-danger-500/10 dark:hover:text-danger-400"
                            aria-label="Remove range"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Custom Item Weights */}
                <div>
                  <div className="mb-2 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => toggleCustomWeights(!useCustomWeights)}
                      className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800 ${
                        useCustomWeights ? 'bg-primary-600 dark:bg-primary-500' : 'bg-gray-200 dark:bg-white/15'
                      }`}
                    >
                      <span
                        className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow-sm ring-1 ring-gray-900/5 transition-transform duration-150 ${
                          useCustomWeights ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    </button>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Use custom item weights</label>
                  </div>
                  <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                    Override default response values with custom scoring weights
                  </p>
                  
                  {useCustomWeights && formData.included_questions.length > 0 && (() => {
                    // Filter to only questions with discrete response options (exclude VAS/slider)
                    const questionsWithOptions = formData.included_questions
                      .map(qId => {
                        const question = questions.find(q => q.id === qId);
                        if (!question) return null;
                        const responseOptions = getQuestionResponseOptions(question);
                        return responseOptions.length > 0 ? { qId, question, responseOptions } : null;
                      })
                      .filter(Boolean);
                    
                    if (questionsWithOptions.length === 0) {
                      return (
                        <p className="text-sm italic text-gray-500 dark:text-gray-400">
                          No questions with discrete response options selected. VAS/slider questions use rescaling instead.
                        </p>
                      );
                    }
                    
                    return (
                      <div className="max-h-48 space-y-3 overflow-y-auto rounded-lg border border-gray-200/70 bg-gray-50 p-3 dark:border-white/[0.07] dark:bg-white/[0.03]">
                        {questionsWithOptions.map(({ qId, question, responseOptions }) => {
                          const questionWeights = formData.item_weights[qId] || {};
                          const hasCustomWeights = Object.keys(questionWeights).length > 0;
                          
                          return (
                            <div key={qId} className="rounded-lg border border-gray-200/80 bg-white p-3 dark:border-white/[0.07] dark:bg-gray-800">
                              <div className="mb-2 flex items-center justify-between">
                                <span className="max-w-xs truncate text-sm font-medium text-gray-700 dark:text-gray-200">
                                  Q{question.order || questions.indexOf(question) + 1}: {(question.text || question.question_text || '').substring(0, 40)}...
                                </span>
                                {hasCustomWeights && (
                                  <button
                                    type="button"
                                    onClick={() => resetQuestionWeights(qId)}
                                    className="text-xs font-medium text-gray-500 transition-colors hover:text-danger-600 dark:text-gray-400 dark:hover:text-danger-400"
                                  >
                                    Reset
                                  </button>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {responseOptions.map((opt, idx) => {
                                  const optValue = opt.value ?? opt.numeric_value ?? idx;
                                  const currentWeight = questionWeights[String(optValue)] ?? optValue;
                                  return (
                                    <div key={idx} className="flex items-center gap-1 text-xs">
                                      <span className="w-6 text-right text-gray-500 dark:text-gray-400">{optValue}→</span>
                                      <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9\-\.]*"
                                        value={currentWeight}
                                        onChange={(e) => updateItemWeight(qId, optValue, e.target.value)}
                                        className="input h-7 w-12 px-1.5 text-center text-xs"
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  
                  {useCustomWeights && formData.included_questions.length === 0 && (
                    <p className="text-sm italic text-gray-500 dark:text-gray-400">Select questions above to configure weights</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Active Toggle */}
          <div className="option-row items-center">
            <div>
              <span className="text-[13.5px] font-medium text-gray-900 dark:text-gray-50">Active</span>
              <p className="mt-0.5 text-[12.5px] text-gray-500 dark:text-gray-400">Calculate this score on survey completion</p>
            </div>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
              className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800 ${
                formData.is_active ? 'bg-primary-600 dark:bg-primary-500' : 'bg-gray-200 dark:bg-white/15'
              }`}
            >
              <span
                className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm ring-1 ring-gray-900/5 transition-transform duration-150 ${
                  formData.is_active ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="btn btn-primary"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {rule ? 'Save Changes' : 'Create Rule'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ScoringTab;
